/**
 * Administration — the people who can sign in, and who is still to accept.
 */
import { useMemo, useState } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import FormTextField from '../../shared/components/form/text-field';
import FormErrorBox from '../../shared/components/form/error';
import { useToast } from '../../provider/toast';
import {
	useCreateUserMutation,
	useInvitesQuery,
	useMembersQuery,
	useRevokeInviteMutation,
	useUpdateUserMutation,
} from '../../queries/firm';
import { useAuth } from '../../provider/auth';
import { count, dateTime, humanise } from '../../utils/format';
import { Card, Dialog, ErrorNotice, Spinner } from '../../shared/ui';
import { Select } from '../../shared/fields';
import { InfoHint } from '../../shared/InfoHint';
import { ROLE_BLURB, ROLE_LABEL, ROLE_OPTIONS } from '../../shared/roles';
import {
	DataTable,
	FilterBar,
	useFullScreen,
	type Column,
} from '../shared/tables';
import { EmptyState } from '../shared/states';
import { ApiError } from '../../api/errors';
import type { InviteRow, Role, User } from '../../schemas';
import { createUserFormSchema, type CreateUserForm } from '../../schemas/forms';
import { PageHeader } from '../../provider/pageTitle';
import { Box, Button, Stack, Typography } from '@mui/material';

/* ═══ what the page reads ══════════════════════════════════════════════════
   `GET /auth/invites` (`authService.listInvites` → `toWireInvite`) — a
   different, smaller shape than `types/index.ts`'s own `Invite` (which
   mirrors the raw Prisma row, token included, for the accept-invite screen
   instead). This is the wire shape that endpoint actually answers with. */

/**
 * One row of the table.
 *
 * A pending invitation is a person who is being given access, so it belongs
 * in the list of people who have it — not in a second table underneath. The
 * union keeps them honestly distinct: you cannot enable or disable an
 * invitation, and only a member has a role you can hand-edit in place.
 */
type PersonRow =
	| { kind: 'member'; id: string; member: User }
	| { kind: 'invite'; id: string; invite: InviteRow };

export default function Administration() {
	const { user } = useAuth();

	const members = useMembersQuery();
	const invites = useInvitesQuery();

	const [creating, setCreating] = useState(false);
	const [viewing, setViewing] = useState<User | null>(null);

	const toast = useToast();
	const updateUser = useUpdateUserMutation();
	const revokeInvite = useRevokeInviteMutation();

	const changeRole = {
		isPending: updateUser.isPending,
		error: updateUser.error,
		run: (id: string, role: Role) =>
			updateUser.mutate(
				{ id, role },
				{ onSuccess: () => toast('ok', 'Role updated.') },
			),
	};

	/* No toast: the row itself changes in front of the person who clicked, and
     a confirmation for something already visible is noise. */
	const changeStatus = {
		isPending: updateUser.isPending,
		error: updateUser.error,
		run: (id: string, status: User['status']) =>
			updateUser.mutate({ id, status }),
	};

	const revoke = {
		isPending: revokeInvite.isPending,
		error: revokeInvite.error,
		run: (id: string) =>
			revokeInvite.mutate(id, {
				onSuccess: () => toast('ok', 'Invitation revoked.'),
			}),
	};

	const people: PersonRow[] = useMemo(() => {
		const rows: PersonRow[] = (members.data?.users ?? []).map((member) => ({
			kind: 'member',
			id: member.pkUserId,
			member,
		}));
		/* Only invitations still in play. An accepted one is already a member and
       would appear twice; an expired one is not something to act on. */
		for (const invite of invites.data?.invites ?? []) {
			if (invite.status === 'PENDING') {
				rows.push({ kind: 'invite', id: invite.pkInviteId, invite });
			}
		}
		return rows;
	}, [members.data, invites.data]);

	return (
		<div className="stack">
			<PageHeader
				title="Administration"
				hint={
					<InfoHint>
						The people who can sign in, what they may do, and who is
						still to accept. Every rule here is enforced by the API,
						not just this screen.
					</InfoHint>
				}
			/>
			<ErrorNotice error={members.error} onRetry={members.refetch} />
			<ErrorNotice error={invites.error} onRetry={invites.refetch} />
			<ErrorNotice error={changeRole.error} />
			<ErrorNotice error={changeStatus.error} />

			<UsersTab
				rows={people}
				loading={members.isPending}
				selfId={user?.pkUserId}
				rolePending={changeRole.isPending}
				statusPending={changeStatus.isPending}
				revokePending={revoke.isPending}
				onOpen={setViewing}
				onRole={(id, role) => changeRole.run(id, role)}
				onToggleStatus={(member) =>
					changeStatus.run(
						member.pkUserId,
						member.status === 'ACTIVE' ? 'DISABLED' : 'ACTIVE',
					)
				}
				onRevoke={(id) => revoke.run(id)}
				onCreate={() => setCreating(true)}
			/>

			{/* Everything the row used to stack into three lines lives here on
          demand, at no cost to the table when nobody has asked for it. */}
			<Dialog
				open={Boolean(viewing)}
				onClose={() => setViewing(null)}
				title={viewing?.name ?? ''}
				subtitle={viewing ? ROLE_LABEL[viewing.role] : undefined}
			>
				{viewing && (
					<dl className="kv">
						<dt>Email</dt>
						<dd>{viewing.email}</dd>
						<dt>Job title</dt>
						<dd>
							{viewing.jobTitle || (
								<span className="mut">not set</span>
							)}
						</dd>
						<dt>Role</dt>
						<dd>{ROLE_LABEL[viewing.role]}</dd>
						<dt>What that allows</dt>
						<dd>{ROLE_BLURB[viewing.role]}</dd>
						<dt>Status</dt>
						<dd>{humanise(viewing.status)}</dd>
						<dt>Last signed in</dt>
						<dd>
							{viewing.lastLoginAt
								? dateTime(viewing.lastLoginAt)
								: 'never'}
						</dd>
					</dl>
				)}
			</Dialog>

			<CreateUserDialog
				open={creating}
				onClose={() => setCreating(false)}
			/>
		</div>
	);
}

/* ═══ users and invitations ════════════════════════════════════════════════
   Role, and enabling or disabling access, are two things you can do to one
   person — both controls sit on the same row, and an invitation gets the one
   action that applies to it. */

function UsersTab({
	rows,
	loading,
	selfId,
	rolePending,
	statusPending,
	revokePending,
	onOpen,
	onRole,
	onToggleStatus,
	onRevoke,
	onCreate,
}: {
	rows: PersonRow[];
	loading: boolean;
	selfId: string | undefined;
	rolePending: boolean;
	statusPending: boolean;
	revokePending: boolean;
	onOpen: (member: User) => void;
	onRole: (id: string, role: Role) => void;
	onToggleStatus: (member: User) => void;
	onRevoke: (id: string) => void;
	onCreate: () => void;
}) {
	const [filters, setFilters] = useState({
		search: '',
		role: '',
		status: '',
	});
	const fullScreen = useFullScreen('Users and pending invitations');

	const visible = rows.filter((row) => {
		const name = row.kind === 'member' ? row.member.name : row.invite.email;
		const email =
			row.kind === 'member' ? row.member.email : row.invite.email;
		const role = row.kind === 'member' ? row.member.role : row.invite.role;
		const status = row.kind === 'member' ? row.member.status : 'INVITED';
		const term = filters.search.trim().toLowerCase();
		if (term && !`${name} ${email}`.toLowerCase().includes(term))
			return false;
		if (filters.role && role !== filters.role) return false;
		if (filters.status && status !== filters.status) return false;
		return true;
	});

	const columns: Array<Column<PersonRow>> = [
		{
			key: 'name',
			label: 'Name',
			width: 240,
			sortValue: (row) =>
				row.kind === 'member' ? row.member.name : row.invite.email,
			render: (row) =>
				row.kind === 'invite' ? (
					<span className="trunc">{row.invite.email}</span>
				) : (
					<span className="namerow">
						<button
							type="button"
							className="step-open trunc"
							title={`${row.member.name} · ${row.member.email}`}
							onClick={() => onOpen(row.member)}
						>
							{row.member.name}
						</button>
						{row.member.pkUserId === selfId && (
							<span className="bdg gray plain">you</span>
						)}
					</span>
				),
		},
		{
			key: 'role',
			label: 'Role',
			width: 190,
			sortValue: (row) =>
				ROLE_LABEL[
					row.kind === 'member' ? row.member.role : row.invite.role
				],
			render: (row) =>
				row.kind === 'invite' ? (
					<span className="small">{ROLE_LABEL[row.invite.role]}</span>
				) : (
					<Select
						value={row.member.role}
						disabled={row.member.pkUserId === selfId || rolePending}
						ariaLabel={`Role for ${row.member.name}`}
						options={ROLE_OPTIONS}
						onChange={(role) =>
							onRole(row.member.pkUserId, role as Role)
						}
					/>
				),
		},
		{
			key: 'status',
			label: 'Status',
			width: 100,
			sortValue: (row) =>
				row.kind === 'member' ? row.member.status : 'INVITED',
			render: (row) =>
				row.kind === 'invite' ? (
					<span
						className="bdg warn plain"
						title={`Expires ${dateTime(row.invite.expiresAt)}`}
					>
						Invited
					</span>
				) : row.member.status === 'ACTIVE' ? (
					<span className="small mut">Active</span>
				) : (
					<span className="bdg gray plain">
						{humanise(row.member.status)}
					</span>
				),
		},
		{
			key: 'lastLogin',
			label: 'Last signed in',
			width: 160,
			sortValue: (row) =>
				row.kind === 'member' ? (row.member.lastLoginAt ?? '') : '',
			render: (row) =>
				row.kind === 'invite' ? (
					<span className="small mut">—</span>
				) : (
					<span className="small mut">
						{row.member.lastLoginAt
							? dateTime(row.member.lastLoginAt)
							: 'never'}
					</span>
				),
		},
		{
			key: 'act',
			label: '',
			width: 130,
			className: 'nowrap',
			render: (row) => {
				if (row.kind === 'invite') {
					return (
						<Button
							variant="outlined"
							size="small"

							disabled={revokePending}
							onClick={() => onRevoke(row.invite.pkInviteId)}
						>
							Revoke
						</Button>
					);
				}
				if (row.member.pkUserId === selfId)
					return <span className="small mut">You</span>;
				return (
					<Button
						variant="outlined"
						size="small"

						disabled={statusPending}
						onClick={() => onToggleStatus(row.member)}
					>
						{row.member.status === 'ACTIVE' ? 'Disable' : 'Enable'}
					</Button>
				);
			},
		},
	];

	return (
		<>
			<FilterBar
				specs={[
					{
						key: 'search',
						label: 'Search',
						kind: 'search',
						placeholder: 'Name or email',
					},
					{
						key: 'role',
						label: 'Role',
						kind: 'select',
						options: [
							{ value: '', label: 'All roles' },
							...ROLE_OPTIONS.map((option) => ({
								value: option.value,
								label: option.label,
							})),
						],
					},
					{
						key: 'status',
						label: 'Status',
						kind: 'select',
						options: [
							{ value: '', label: 'All statuses' },
							{ value: 'ACTIVE', label: 'Active' },
							{ value: 'DISABLED', label: 'Disabled' },
							{ value: 'INVITED', label: 'Invited' },
						],
					},
				]}
				values={filters}
				onChange={(key, value) =>
					setFilters((current) => ({ ...current, [key]: value }))
				}
				onClear={() => setFilters({ search: '', role: '', status: '' })}
				resultNote={`${count(visible.length)} of ${count(rows.length)}`}
				actions={
					<>
						<Button
							variant="contained"
							size="small"
							onClick={onCreate}
						>
							Create user
						</Button>
						<InfoHint label="About this list">
							Everyone who can sign in, and everyone who has been
							asked to. Disabling is reversible; the account and
							everything it has done stay on the record.
						</InfoHint>
						{fullScreen.button}
					</>
				}
			/>
			<Card>
				{loading ? (
					<Spinner />
				) : (
					<DataTable
						rows={visible}
						columns={columns}
						rowKey={(row) => `${row.kind}-${row.id}`}
						caption="Users and pending invitations"
						fullScreen={fullScreen}
						maxHeight="main"
						initialSort={{ key: 'name', direction: 'asc' }}
						empty={
							<EmptyState title="Nobody matches those filters">
								Clear the filters to see everyone with access.
							</EmptyState>
						}
					/>
				)}
			</Card>
		</>
	);
}

/* ═══ creating a user ══════════════════════════════════════════════════════
   An administrator adds a colleague directly — name, email, a first password
   and a role — with `POST /users`. They sign in with that password straight
   away; the administrator tells them what it is. */

const EMPTY: CreateUserForm = {
	name: '',
	email: '',
	password: '',
	role: 'AUDITOR',
};

/** What the server said about one field, onto that field. */
const FIELDS = ['name', 'email', 'password', 'role'] as const;

function CreateUserDialog({
	open,
	onClose,
}: {
	open: boolean;
	onClose: () => void;
}) {
	const toast = useToast();
	const create = useCreateUserMutation();
	/* Checked as they type (`mode: 'onChange'`); the server checks it again. */
	const {
		control,
		handleSubmit,
		reset,
		setError,
		watch,
		formState: { errors, isValid },
	} = useForm<CreateUserForm>({
		resolver: zodResolver(createUserFormSchema),
		mode: 'onChange',
		defaultValues: EMPTY,
	});
	const role = watch('role');

	const close = () => {
		reset(EMPTY);
		create.reset();
		onClose();
	};

	const submit = handleSubmit((values) =>
		create.mutate(values, {
			onSuccess: (user) => {
				toast('ok', `${user.name} can now sign in.`);
				close();
			},
			onError: (error) => {
				if (!(error instanceof ApiError)) return;
				for (const [field, message] of Object.entries(error.fields)) {
					if ((FIELDS as readonly string[]).includes(field)) {
						setError(field as (typeof FIELDS)[number], { message });
					}
				}
			},
		}),
	);

	return (
		<Dialog
			open={open}
			onClose={close}
			title="Create user"
			subtitle="They can sign in straight away with this email and password."
			footer={
				<Box
					sx={{
						display: 'flex',
						gap: 'var(--s2)',
						justifyContent: 'flex-end',
					}}
				>
					<Button
						variant="outlined"
						onClick={close}
						disabled={create.isPending}
					>
						Cancel
					</Button>
					<Button
						variant="contained"
						loading={create.isPending}
						disabled={!isValid || create.isPending}
						onClick={() => void submit()}
					>
						Create user
					</Button>
				</Box>
			}
		>
			<Stack gap="var(--s2)">
				<ErrorNotice error={create.error} />
				<Box>
					<FormTextField
						control={control}
						name="name"
						label="Name"
						isRequired
						muiProps={{ autoFocus: true }}
					/>
					<FormErrorBox errors={[errors.name]} />
				</Box>
				<Box>
					<FormTextField
						control={control}
						name="email"
						label="Email"
						isRequired
						muiProps={{
							type: 'email',
							helperText: 'Small letters only, with an @.',
						}}
					/>
					<FormErrorBox errors={[errors.email]} />
				</Box>
				<Box>
					<FormTextField
						control={control}
						name="password"
						label="Password"
						isRequired
						muiProps={{
							type: 'password',
							autoComplete: 'new-password',
							helperText:
								'At least 8 characters, with a capital letter, a small letter, a number and a special character.',
						}}
					/>
					<FormErrorBox errors={[errors.password]} />
				</Box>
				<Box>
					<Typography
						variant="body2"
						fontWeight={600}
						sx={{ mb: '6px' }}
					>
						Role *
					</Typography>
					<Controller
						control={control}
						name="role"
						render={({ field }) => (
							<Select
								id="create-user-role"
								value={field.value}
								options={ROLE_OPTIONS}
								onChange={(next) =>
									field.onChange(next as Role)
								}
							/>
						)}
					/>
					<Typography
						sx={{
							mt: '6px',
							fontSize: 'var(--fs-small)',
							color: 'var(--ink-3)',
						}}
					>
						{ROLE_BLURB[role]}
					</Typography>
				</Box>
			</Stack>
		</Dialog>
	);
}
